import './App.css'
import { AppHeader } from './header/AppHeader.tsx'
import { Outlet } from 'react-router-dom'
import AppSidebarOutlet from './sidebar/AppSidebarOutlet.tsx'
import * as React from 'react'
import { useEffect, useLayoutEffect } from 'react'
import clsx from 'clsx'
import { sidebarContext } from './sidebar/SidebarContextProvider.tsx'
import * as localStorageUtils from '../../common/local-storage/local-storage-utils'
import { localStorageKeys } from '../configs/local-storage-keys'
import type { IAppLifecycleService } from '../lifecycle/AppLifecycleService'
import { useDiRef } from '@/common/react/di-hooks'
import { TutorialProvider } from '@/tutorial/TutorialContext'
import { TutorialOverlay } from '@/tutorial/TutorialOverlay'
import { RightPanelProvider } from '@/core/right-panel/RightPanelContext'
import { RightPanelSlot } from '@/core/right-panel/RightPanelSlot'

const SIDEBAR_OPEN_WIDTH_VAR = '--left-sidebar-open-width'

function App() {
  const appLifecycleService = useDiRef<IAppLifecycleService>('IAppLifecycleService')
  useEffect(() => {
    if (!appLifecycleService.isOnAfterFirstRenderEmitted) {
      appLifecycleService.emitOnAfterFirstRender()
    }
  }, [])

  const [sidebarOpen, setSidebarOpen] = React.useState(() => {
    const storedValue = localStorageUtils.readObject(localStorageKeys.SidebarOpen) as
      | boolean
      | undefined
    return storedValue ?? true
  })

  const [sidebarWidth, setSidebarWidth] = React.useState(
    () => localStorageUtils.readNumber(localStorageKeys.SidebarWidth) ?? 250,
  )

  useLayoutEffect(() => {
    document.documentElement.style.setProperty(SIDEBAR_OPEN_WIDTH_VAR, `${sidebarWidth}px`)
  }, [sidebarWidth])

  function setSidebarOpenWrapper(value: boolean) {
    setSidebarOpen(value)
    localStorageUtils.writeBool(localStorageKeys.SidebarOpen, value)
  }

  function setSidebarWidthWrapper(value: number) {
    setSidebarWidth(value)
    localStorageUtils.writeNumber(localStorageKeys.SidebarWidth, value)
  }

  return (
    <TutorialProvider>
      <RightPanelProvider>
        <div className={clsx('app', { sidebarOpen })}>
          <AppHeader />
          <sidebarContext.Provider
            value={{
              sidebarOpen,
              setSidebarOpen: setSidebarOpenWrapper,
              sidebarWidth,
              setSidebarWidth: setSidebarWidthWrapper,
            }}
          >
            <AppSidebarOutlet />
          </sidebarContext.Provider>
          <div className="page-container">
            <Outlet context="page" />
          </div>
          <RightPanelSlot />
        </div>
      </RightPanelProvider>
      <TutorialOverlay />
    </TutorialProvider>
  )
}

export default App
